import React, {useEffect, useState, useCallback, useRef} from 'react';

import { makeStyles } from '@material-ui/core/styles';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    CircularProgress,
} from '@material-ui/core';
import {useHistory} from "react-router-dom";
import { green , blue, red } from '@material-ui/core/colors';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Typography from '@material-ui/core/Typography'
import Skeleton from '@material-ui/lab/Skeleton'

import AuthProvider from '@user-provider/AuthProvider'
import PrescriptionService from '@user-service/PrescriptionService'

import DoneIcon from '@material-ui/icons/Done';
import clsx from "clsx";
import MeasureSelectDialog from "./MeasureSelectDialog";
import MeasureSelectionToggle from "./MeasureSelectionToggle";
import ReplayIcon from '@material-ui/icons/Replay';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import IconButton from "@material-ui/core/IconButton";
import BridgeProvider from "../../../provider/BridgeProvider";
import DeviceStatusBox from "../../../components/DeviceStatusBox";
import MeasureAllHelper from "../../../helper/MeasureAllHelper";


const MeasureSelect = ({history}) => {
    
    const [noiseCode] = useState('A')
    
    return (
        <div id="cont-wrap">
            <div className="sv">
                <div>
                    <h3>뇌기능 측정</h3>
                    <span>EEG, 균형상태, 의식패턴을 통해 뇌의 기능을 측정합니다.</span>
                    <i>Brain Health</i>
                </div>
            </div>
            <div className="cont">
                <Box mt={10} style={{margin: 'auto'}}>
                    <MeasureSelectBox history={history} noiseCode={noiseCode}/>
                </Box>
            </div>
        </div>
    );
}

//로딩 스켈레톤 화면 
export function LoadingSkeleton() {
    const classes = useStyles();

    return (
        <Box className={classes.root}>
            <div className="editprofile_wrap wrapper">
                <Skeleton variant="text" />
                <Skeleton variant="rect" width={210} height={118} />
            </div>
        </Box>
    )
}

export function MeasureSelectBox(){
    const [open, setOpen] = useState(false)
    const [state, setState] = useState({
        prescription: null,
        request: null
    })
    const [measureAllStatus, setMeasureAllStatus] = useState(null) // null | 'running' | 'done'
    const [measureAllCountdown, setMeasureAllCountdown] = useState(null)
    const measureAllTimerRef = useRef(null)
    const countdownIntervalRef = useRef(null)

    const clearMeasureAllTimers = () => {
        if(measureAllTimerRef.current){
            clearTimeout(measureAllTimerRef.current)
            measureAllTimerRef.current = null
        }
        if(countdownIntervalRef.current){
            clearInterval(countdownIntervalRef.current)
            countdownIntervalRef.current = null
        }
    }

    const fetch = useCallback(async () => {
        let prescription = await PrescriptionService.getLatest();

        if(!prescription){
          const measureTypes = ["eeg","balance","meditationTest","measureBalance","measureBandTest"]
          await PrescriptionService.newPrescription(measureTypes)
          prescription = await PrescriptionService.getLatest();
        }

        setState({
            prescription: !prescription ? {} : prescription,
            request : prescription ? await PrescriptionService.getReadyRequests(prescription.id) : null
        })

    }, [])


    useEffect(()=>{
        fetch().then()
    },[fetch])




    const { prescription, request } = state

    // 해당 측정을 지금 시작할 수 있는지 (미완료 + 처방에 포함 + 요청/권한 존재)
    const canMeasureStep = (step) => {
        if(!prescription) return false
        if('yes' === prescription[step.doneKey]) return false
        if(-1 === (prescription.measureTypes || []).indexOf(step.measureType)) return false
        const isRequest = request ? request[step.requestKey] : null
        return !!(isRequest || AuthProvider.isAdmin() || AuthProvider.isProfileSelectorView())
    }

    const getNextMeasureStep = () => MeasureAllHelper.steps.find(canMeasureStep)

    const goMeasureStep = (step) => {
        MeasureAllHelper.setCurrent(step.measureType)
        const requestID = request ? request[step.requestIDKey] : null
        document.location.href = MeasureAllHelper.getMeasureUrl(step.measureType, prescription.id, requestID)
    }

    const measureAllStart = () => {
        const next = getNextMeasureStep()
        if(!next) return
        // 이동하는 동안 카드에 회전 인디케이터를 바로 표시한다
        setMeasureAllStatus('running')
        MeasureAllHelper.start(prescription.id)
        goMeasureStep(next)
    }

    const measureAllStop = () => {
        clearMeasureAllTimers()
        MeasureAllHelper.clear()
        setMeasureAllStatus(null)
        setMeasureAllCountdown(null)
    }

    // 측정 페이지에서 복귀 시 다음 측정으로 자동 진행
    useEffect(() => {
        if(!prescription || !prescription.id) return

        const mode = MeasureAllHelper.get()
        if(!mode){
            return
        }

        // 신규 측정 등으로 회차가 바뀌면 이전 회차의 진행상태를 리셋한다
        if(String(mode.prescriptionID) !== String(prescription.id)){
            MeasureAllHelper.clear()
            setMeasureAllStatus(null)
            return
        }

        // 직전 측정이 완료되지 않은 채 돌아온 경우 = 사용자가 중단함 → 모드 해제
        const currentStep = MeasureAllHelper.steps.find(s => s.measureType === mode.current)
        if(currentStep && 'yes' !== prescription[currentStep.doneKey]){
            MeasureAllHelper.clear()
            setMeasureAllStatus(null)
            return
        }

        const next = MeasureAllHelper.steps.find(canMeasureStep)
        if(!next){
            MeasureAllHelper.clear()
            setMeasureAllStatus('done')
            return
        }

        setMeasureAllStatus('running')

        // 5초 카운트다운 후 다음 측정으로 이동
        setMeasureAllCountdown(5)
        countdownIntervalRef.current = setInterval(() => {
            setMeasureAllCountdown(prev => (prev && 1 < prev) ? prev - 1 : prev)
        }, 1000)

        measureAllTimerRef.current = setTimeout(() => {
            if(MeasureAllHelper.isActive(prescription.id)){
                goMeasureStep(next)
            }
        }, 5000)

        return () => {
            clearMeasureAllTimers()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state])

    const measureNew = async (measureTypes) => {
        // 신규 측정 회차가 생성되면 4대 측정 진행상태를 리셋한다
        measureAllStop()
        await PrescriptionService.updateStatus(prescription.id, 'eeg', '40','C','done')
        await PrescriptionService.newPrescription(measureTypes)
        fetch().then(() => {setOpen(false)})
    }

    const newPrescription = () => {
       setOpen(true)
    }
    
    const closeDialog = () => {
        setOpen(false)
    }

    if(!prescription){
        return <LoadingSkeleton />
    }

    const measurableSteps = MeasureAllHelper.steps.filter(step =>
        -1 < (prescription.measureTypes || []).indexOf(step.measureType)
    )
    const totalCount = measurableSteps.length || MeasureAllHelper.steps.length
    const doneCount = (measurableSteps.length ? measurableSteps : MeasureAllHelper.steps)
        .filter(step => 'yes' === prescription[step.doneKey]).length
    const nextStep = getNextMeasureStep()

    return (
        <Box>
            <Box mb={3}>
                <Grid container>
                    <Grid item xs={4}>
                        <Typography variant={'h5'}>{prescription.id}회차</Typography>
                        <Typography variant={'subtitle2'}>뇌기능 분석메뉴에서 결과를 확인 할 수 있습니다.</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <MeasureSelectionToggle measureTypes={prescription.measureTypes} prescriptionID={prescription.id}/>
                    </Grid>
                    <Grid item xs={2}>
                        <Grid container spacing={1} justify={'flex-end'} style={{marginTop: 10}}>
                            <Grid item>
                                <IconButton aria-label="새로고침" style={{padding: 7, border: `1px solid #BEBEBE`}} onClick={() => { fetch().then() }}>
                                    <ReplayIcon/>
                                </IconButton>
                            </Grid>
                            {(AuthProvider.isAdmin() || AuthProvider.isProfileSelectorView() )&&
                            <Grid item>
                                <Button
                                    onClick={newPrescription}
                                    className={'common-button'}
                                    endIcon={<ChevronRightIcon className={'rightIcon'}/>}
                                >신규측정</Button>    
                            </Grid>
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            
            <Grid container spacing={3} alignItems={"stretch"} style={{marginTop: 70}}>
                <Grid item xs={12} md={5} style={{display: 'flex'}}>
                    <MeasureAllCard
                        status={measureAllStatus}
                        countdown={measureAllCountdown}
                        doneCount={doneCount}
                        totalCount={totalCount}
                        nextStep={nextStep}
                        onStart={measureAllStart}
                        onStop={measureAllStop}
                        onDismiss={() => setMeasureAllStatus(null)}
                    />
                </Grid>
                <Grid item xs={12} md={7}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <MeasureType
                                name="EEG 측정"
                                description="좌/우 뇌상태를 3D FFT로 측정."
                                order={1}
                                color={'blue'}
                                background={blue[100]}
                                isMeasure={prescription.isEEG}
                                countMeasure={prescription.countEEG}
                                prescription={prescription}
                                isRequest={request ? request.eeg : null}
                                requestID={request ? request.eegRequestID : null}
                                requestDate={request ? request.eegRequestDate : null}
                                measureType={'eeg'}
                                />
                        </Grid>
                        <Grid item xs={6}>
                            <MeasureType
                                name="균형상태 측정"
                                description="안정, 주의력, 집중력 상태를 측정"
                                order={2}
                                color={'green'}
                                background={green[100]}
                                isMeasure={prescription.isBalance}
                                countMeasure={prescription.countBalance}
                                prescription={prescription}
                                isRequest={request ? request.default : null}
                                requestID={request ? request.defaultRequestID : null}
                                requestDate={request ? request.defaultRequestDate : null}
                                measureType={'balance'}
                                />
                        </Grid>
                        <Grid item xs={6}>
                            <MeasureType
                                name="뇌기능 의식패턴"
                                description="뇌기능 의식패턴을 측정"
                                order={3}
                                color={'red'}
                                background={red[100]}
                                isMeasure={prescription.isBrainActive}
                                countMeasure={prescription.countBrainActive}
                                prescription={prescription}
                                isRequest={request ? request.meditation : null}
                                requestID={request ? request.meditationRequestID : null}
                                requestDate={request ? request.meditationRequestDate : null}
                                measureType={'meditationTest'}
                                />
                        </Grid>
                        <Grid item xs={6}>
                            <MeasureType
                                name="항상성 자기조절"
                                description="항상성 자기조절 측정"
                                order={4}
                                color={'red'}
                                background={red[100]}
                                isMeasure={prescription.isMeasureBandTest}
                                countMeasure={'yes' === prescription.isMeasureBandTest ? 1 : 0}
                                prescription={prescription}
                                isRequest={request ? request.measureBandTest : null}
                                requestID={request ? request.measureBandTestRequestID : null}
                                requestDate={request ? request.measureBandTestRequestDate : null}
                                measureType={'measureBandTest'}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <DeviceStatusBox />
          
            <MeasureSelectDialog
                open={open}
                title={'신규측정'}
                close={() => {
                    closeDialog()
                }}
                submit={async (measureList) => {
                    closeDialog()
                    await measureNew(measureList)
                }}
                buttonName={'신규 측정 생성'}
            />
        </Box>
    )
}


/**
 * 4대 측정 카드 - 4개 측정을 순서대로 자동 진행
 * 측정 카드(MeasureType)와 동일한 카드 형식으로 표시한다.
 */
export function MeasureAllCard(props) {
    const classes = useStyles();
    const { status, countdown, doneCount, totalCount, nextStep, onStart, onStop, onDismiss } = props

    const isRunning = 'running' === status
    const isDone = 'done' === status
    const canStart = !isRunning && !isDone && !!nextStep

    const handleClick = () => {
        if(isRunning){
            // 진행 중에는 취소 버튼으로만 중지한다
            return
        } else if(isDone){
            onDismiss()
        } else if(canStart){
            onStart()
        }
    }

    const isAllMeasured = !isRunning && !isDone && 0 < totalCount && doneCount >= totalCount
    const isComplete = isDone || isAllMeasured

    return (
        <Card
            className={clsx(classes.card, classes.allCard, {
                [classes.disableMeasure]: !canStart && !isRunning && !isComplete,
                [classes.cardDone]: isComplete,
            })}
            onClick={handleClick} elevation={0}
        >
            {isComplete &&
            <Box className={classes.doneBadge}>
                <DoneIcon style={{fontSize: 20}}/>
            </Box>
            }
            <CardContent className={classes.allCardContent}>
                <Box display="flex" className={classes.allCardBody} alignItems="center">
                    <Box width="100%">
                        <Box display="flex" alignItems="center" className={clsx(classes.statusIcon, classes.allStatusIcon)} justifyContent={'center'} mb={3}>
                            {isComplete
                                ? <DoneIcon className={clsx('icon', 'done')}/>
                                : isRunning
                                    ? (countdown
                                        ? <span className={classes.countdownNum}>{countdown}</span>
                                        : <CircularProgress size={56} style={{color: blue[700]}}/>)
                                    : <PlayArrowIcon className={clsx('icon', 'yet', {
                                        'yetDisabled': !canStart
                                    })}/>
                            }
                        </Box>
                        <Typography gutterBottom className={clsx('title', 'allTitle', {'allTitleRunning': isRunning && nextStep})} component="h2" align="center">
                            {isRunning && nextStep
                                ? <React.Fragment><span className={classes.nextStepName}>{nextStep.shortName}</span> 측정이 시작됩니다</React.Fragment>
                                : '4대 측정'
                            }
                        </Typography>
                        <Typography variant="body2" className={'desc'} align="center">
                            {isRunning
                                ? (nextStep ? '잠시 후 측정이 시작됩니다' : '측정 상태를 확인하고 있습니다')
                                : '4가지 측정을 순서대로 자동 진행'
                            }
                        </Typography>

                        {isRunning &&
                        <React.Fragment>
                            <Typography className={classes.allCount} align="center">
                                {doneCount} / {totalCount}
                            </Typography>
                            <Box mt={3} display="flex" justifyContent="center">
                                <Button
                                    className={classes.cancelButton}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onStop()
                                    }}
                                >취소</Button>
                            </Box>
                        </React.Fragment>
                        }
                    </Box>
                </Box>
            </CardContent>
        </Card>
    )
}

export function MeasureType(props) {
    const classes = useStyles();
    const { isRequest, isMeasure, prescription, requestID, measureType, requestDate } = props
    const history = useHistory()
    let isAble = -1 < (prescription.measureTypes || []).indexOf(measureType)
    if(isMeasure) isAble = true
    
    const goMeasure = () => {
        
        if(!isAble){
            return
        }
        switch(measureType){
            case 'eeg':
                document.location.href=`/measureBand/${prescription.id}?rqs=${requestID}`
                break
            case 'balance':
                history.push(`/measure/${prescription.id}?rqs=${requestID}`)
                break
            case 'meditationTest':
                history.push(`/exercise/meditationTest/${prescription.id}?rqs=${requestID}`)
                break;
            case 'measureBandTest':
                history.push(`/exercise/measureBandTest/${prescription.id}?rqs=${requestID}`)
                break;
            default:
        }
        
    }
    
    const canMeasure = 'yes' !== isMeasure && (isRequest || AuthProvider.isAdmin() || AuthProvider.isProfileSelectorView())
    const isDone = 'yes' === isMeasure
    return (
        <Grid>
            <Card
                className={clsx(classes.card,{
                    [classes.disableMeasure]: !isAble && !isDone,
                    [classes.cardDone]: isDone,
                })}
                onClick={()=>{
                    if(canMeasure){
                        goMeasure()
                    }
                }} elevation={0}
            >
                {isDone &&
                <Box className={classes.doneBadge}>
                    <DoneIcon style={{fontSize: 20}}/>
                </Box>
                }
                <CardContent >
                    <Box display="flex" className={clsx(classes.cardBody)} alignItems="center">
                        <Box width="100%">
                            <Box display="flex" alignItems="center" justifyContent={'center'} mb={2}>
                                <span className={clsx(classes.orderNum, {
                                    'numDone': isDone,
                                    'numDisabled': !isAble && !isDone
                                })}>{props.order}</span>
                            </Box>
                            <Typography gutterBottom className={'title'} component="h2" align="center">
                                {props.name}
                            </Typography>
                            <Typography variant="body2" className={'desc'} align="center">
                                {props.description}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    )
}


const useStyles = makeStyles(theme => ({
    root: {
       
    },
    headerBox: {
        marginBottom: 40,
        marginTop: 120
    },
    title: {
        fontSize:26,
        fontWeight: 'bold'
    },
    card: {
        position: 'relative',
        minWidth: 200,
        backgroundColor: '#F5F9FE',
        borderRadius: 24,
        border: `1px solid #D6E1F1`,
        cursor: 'pointer',
        '&:hover' : {
            backgroundColor: '#E1E5EC'
        },
        '& .title': {
            fontSize: 26,
            fontWeight: 'bold',
            letterSpacing: -1
        },
        '& .desc' : {
            marginTop: -10
        }
    },
    cardDone: {
        backgroundColor: '#E9FBF5',
        border: `1px solid #00DF97`,
        cursor: 'default',
        '&:hover' : {
            backgroundColor: '#E9FBF5'
        },
    },
    doneBadge: {
        position: 'absolute',
        top: 14,
        left: 14,
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: '#00DF97',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
    },
    disableMeasure: {
        // backgroundColor: '#ededed',
        borderRadius: 30,
        border: `0px solid #D6E1F1`,
        cursor: 'default',

        '& .title': {
            fontWeight: 'bold',
            color: '#999'
        },
    },
    statusIcon: {
        border: '4px solid #DCE8F5'  ,
        width: 56,
        height: 56,
        margin: 'auto',
        borderRadius: '50%',
        marginBottom: 16,

        '& .yet': {
            color: blue[700],
            fontSize: 48,
            fontWeight: 'bold',
        },
        '& .yetDisabled': {
            color: '#999',
            fontSize: 48,
            fontWeight: 'bold',
        },
        '& .done': {
            color: '#00DF97',
            fontSize: 64,
            fontWeight: 'bold',
            marginTop: -18,
            marginLeft: 18
        },
    },
    orderNum: {
        color: blue[700],
        fontSize: 56,
        fontWeight: 900,
        lineHeight: 1,
        letterSpacing: -2,
        '&.numDisabled': {
            color: '#999',
        },
        '&.numDone': {
            color: '#00DF97',
        },
    },
    cardBody: {
        height: 190
    },
    allCard: {
        width: '100%',
        minWidth: 280,

        '& .allTitle': {
            fontSize: 36,
        },
        '& .allTitle.allTitleRunning': {
            fontSize: 27,
        },
    },
    allCardContent: {
        height: '100%',
        boxSizing: 'border-box',
    },
    allCardBody: {
        height: '100%',
        minHeight: 400,
    },
    nextStepName: {
        color: blue[700],
    },
    cancelButton: {
        padding: '8px 40px',
        borderRadius: 20,
        border: '1px solid #BEBEBE',
        backgroundColor: '#fff',
        color: '#666',
        fontWeight: 'bold',
        fontSize: 16,
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    countdownNum: {
        fontSize: 48,
        fontWeight: 900,
        lineHeight: 1,
        letterSpacing: -2,
        color: blue[700],
    },
    allCount: {
        marginTop: 16,
        fontSize: 44,
        fontWeight: 900,
        lineHeight: 1,
        letterSpacing: -2,
        color: blue[700],
    },
    allStatusIcon: {
        width: 96,
        height: 96,
        marginBottom: 30,

        '& .yet': {
            fontSize: 84,
        },
        '& .done': {
            fontSize: 96,
            marginTop: -26,
            marginLeft: 26
        }
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
      },
}));

export default MeasureSelect
